import { FormEvent, useEffect, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material"
import CwDataTable from "../../components/construction/CwDataTable"
import CwPageHeader from "../../components/construction/CwPageHeader"
import CompanyChangeAction from "../../components/construction/CompanyChangeAction"
import { useSelectedCompany } from "../../context/companyProvider"
import { getLocale, t } from "../../i18n"
import { formatCompanyContext } from "../../utils/companyAccess"
import { SageApiError } from "../../utils/sageApiError"
import { timeClockService } from "../../services/timeClockService"
import { CwFlaggedPunch, CwOfficeAlert } from "../../Interfaces"
import {
  SettingsForm,
  alertState,
  alertStatusLabel,
  formToSettings,
  punchDistanceLabel,
  punchKindLabel,
  settingsToForm,
  userName,
} from "../../utils/punchLocations"

function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return ""
  const d = new Date(value)
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString(getLocale() === "es" ? "es-US" : "en-US", {
      timeZone: "America/Los_Angeles",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    })
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof SageApiError && err.message ? err.message : fallback
}

function employeeName(e: { firstName?: string, lastName?: string, employeeNum?: number } | null | undefined): string {
  return [e?.firstName, e?.lastName].filter(Boolean).join(" ").trim() || (e?.employeeNum ? `#${e.employeeNum}` : "")
}

function Section({ title, helper, action, children }: {
  title: string
  helper?: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="mb-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <h2 className="text-xl font-semibold">{title}</h2>
          {helper ? <p className="text-sm text-gray-600 font-source">{helper}</p> : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}

function Loading() {
  return (
    <div className="bg-white rounded-2xl shadow-lg h-32 flex items-center justify-center">
      <CircularProgress />
    </div>
  )
}

function LoadError() {
  return (
    <p className="bg-white rounded-2xl shadow-lg px-5 py-8 text-center text-red-600 font-source">
      {t("timeClock.loadFailed")}
    </p>
  )
}

function OverrideDialog({ alert, onClose, onConfirm }: {
  alert: CwOfficeAlert
  onClose: () => void
  onConfirm: (note: string) => Promise<void>
}) {
  const [note, setNote] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError("")
    try {
      await onConfirm(note.trim())
      onClose()
    } catch (err) {
      setError(errorText(err, t("timeClock.saveFailed")))
    } finally {
      setSaving(false)
    }
  }
  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <form onSubmit={(e) => void submit(e)}>
        <DialogTitle>{t("timeClock.overrideTitle")}</DialogTitle>
        <DialogContent className="flex flex-col gap-4 !pt-2">
          <p className="font-source">{t("timeClock.overrideHelper", { name: employeeName(alert.employee) })}</p>
          <TextField label={t("timeClock.overrideNoteLabel")} value={note} onChange={(e) => setNote(e.target.value)} inputProps={{ maxLength: 255 }} autoFocus />
          {error ? <p role="alert" className="text-red-600 text-sm">{error}</p> : null}
        </DialogContent>
        <DialogActions>
          <button type="button" className="px-4 py-2" onClick={onClose} disabled={saving}>{t("common.cancel")}</button>
          <button type="submit" className="px-4 py-2 rounded-full bg-primary text-white" disabled={saving}>
            {saving ? t("common.saving") : t("timeClock.allow")}
          </button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

function SettingsSection({ companyId, canEdit }: { companyId: number, canEdit: boolean }) {
  const queryClient = useQueryClient()
  const query = useQuery(
    ["cw-time-clock-settings", companyId],
    () => timeClockService.getSettings(companyId),
    { suspense: false, useErrorBoundary: false },
  )
  const [form, setForm] = useState<SettingsForm>(settingsToForm(undefined))
  const [message, setMessage] = useState<{ kind: "ok" | "error", text: string } | null>(null)
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    if (query.data) setForm(settingsToForm(query.data))
  }, [query.data])

  const field = (key: keyof SettingsForm, label: string, helper?: string) => (
    <TextField
      label={label}
      helperText={helper}
      value={form[key]}
      disabled={!canEdit}
      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
      size="small"
    />
  )

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const parsed = formToSettings(form)
    if ("error" in parsed) {
      setMessage({ kind: "error", text: parsed.error })
      return
    }
    setSaving(true)
    setMessage(null)
    try {
      await timeClockService.updateSettings(companyId, parsed.body)
      void queryClient.invalidateQueries(["cw-time-clock-settings", companyId])
      setMessage({ kind: "ok", text: t("timeClock.saved") })
    } catch (err) {
      setMessage({ kind: "error", text: errorText(err, t("timeClock.saveFailed")) })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Section title={t("timeClock.settingsTitle")} helper={t("timeClock.settingsHelper")}>
      {query.isLoading ? <Loading /> : query.error ? <LoadError /> : (
        <form onSubmit={(e) => void submit(e)} className="bg-white rounded-2xl shadow-lg p-5 grid gap-4 md:grid-cols-2">
          {field("address", t("timeClock.addressLabel"))}
          {field("city", t("timeClock.cityLabel"))}
          {field("state", t("timeClock.stateLabel"))}
          {field("zipcode", t("timeClock.zipLabel"))}
          {field("officeLatitude", t("timeClock.latitudeLabel"))}
          {field("officeLongitude", t("timeClock.longitudeLabel"))}
          {field("farPunchThresholdMiles", t("timeClock.thresholdLabel"), t("timeClock.thresholdHelper"))}
          {field("adminCustomPayrollCode", t("timeClock.customCodeLabel"), t("timeClock.customCodeHelper"))}
          {field("adminPayrollJobNumber", t("timeClock.adminJobLabel"), t("timeClock.adminJobHelper"))}
          <div className="md:col-span-2 flex items-center gap-4">
            {canEdit ? (
              <button type="submit" className="px-4 py-2 rounded-full bg-primary text-white" disabled={saving}>
                {saving ? t("common.saving") : t("timeClock.saveSettings")}
              </button>
            ) : null}
            {message ? (
              <p role={message.kind === "error" ? "alert" : "status"} className={message.kind === "error" ? "text-red-600" : "text-green-700"}>
                {message.text}
              </p>
            ) : null}
          </div>
        </form>
      )}
    </Section>
  )
}

export default function TimeClockPage() {
  const { selectedCompany, canManageSageMaster } = useSelectedCompany()
  const companyId = selectedCompany?.id
  const queryClient = useQueryClient()
  const [showClosed, setShowClosed] = useState(false)
  const [showReviewed, setShowReviewed] = useState(false)
  const [from, setFrom] = useState(isoDaysAgo(13))
  const [to, setTo] = useState(isoDaysAgo(0))
  const [overriding, setOverriding] = useState<CwOfficeAlert | null>(null)
  const [actionError, setActionError] = useState("")

  const alertsQuery = useQuery(
    ["cw-office-alerts", companyId, showClosed],
    () => timeClockService.listAlerts(companyId as number, showClosed),
    { enabled: !!companyId, suspense: false, useErrorBoundary: false, refetchInterval: 60_000 },
  )
  const rangeValid = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to
  const flaggedQuery = useQuery(
    ["cw-flagged-punches", companyId, from, to, showReviewed],
    () => timeClockService.listFlagged(companyId as number, from, to, showReviewed),
    { enabled: !!companyId && rangeValid, suspense: false, useErrorBoundary: false },
  )

  const refreshAlerts = () => void queryClient.invalidateQueries(["cw-office-alerts", companyId])

  const dismiss = async (alert: CwOfficeAlert) => {
    if (!companyId) return
    setActionError("")
    try {
      await timeClockService.dismissAlert(companyId, alert.id)
      refreshAlerts()
    } catch (err) {
      setActionError(errorText(err, t("timeClock.saveFailed")))
    }
  }

  const review = async (punch: CwFlaggedPunch) => {
    if (!companyId) return
    setActionError("")
    try {
      await timeClockService.reviewPunch(companyId, punch.id)
      void queryClient.invalidateQueries(["cw-flagged-punches", companyId])
    } catch (err) {
      setActionError(errorText(err, t("timeClock.saveFailed")))
    }
  }

  return (
    <div className="w-full">
      <CwPageHeader
        title={t("timeClock.title")}
        subtitle={selectedCompany ? `${formatCompanyContext(selectedCompany)} · ${t("timeClock.subtitle")}` : t("timeClock.subtitle")}
        action={<CompanyChangeAction />}
      />
      {actionError ? <p role="alert" className="text-red-600 mb-4 font-source">{actionError}</p> : null}

      <Section
        title={t("timeClock.alertsTitle")}
        helper={t("timeClock.alertsHelper")}
        action={(
          <label className="flex items-center gap-2 font-source">
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
            {t("timeClock.showClosed")}
          </label>
        )}
      >
        {alertsQuery.isLoading ? <Loading /> : alertsQuery.error ? <LoadError /> : (
          <CwDataTable<CwOfficeAlert>
            columns={[
              { key: "employee", label: t("timeClock.columnEmployee"), render: (row) => employeeName(row.employee) },
              { key: "date", label: t("timeClock.columnDate"), render: (row) => String(row.date).slice(0, 10) },
              { key: "mode", label: t("timeClock.columnMode"), render: (row) => (row.mode === "ADMIN" ? t("timeClock.modeAdmin") : t("timeClock.modeForeman")) },
              { key: "jobSite", label: t("timeClock.columnJobSite"), render: (row) => row.jobSite?.shortName || row.jobSite?.name || "" },
              { key: "attempts", label: t("timeClock.columnAttempts"), render: (row) => String(row.attemptCount) },
              {
                key: "last",
                label: t("timeClock.columnLastAttempt"),
                render: (row) => `${formatDateTime(row.lastAttemptAt)}${row.attemptedByUser ? ` · ${userName(row.attemptedByUser)}` : ""}`,
              },
              {
                key: "status",
                label: t("timeClock.columnStatus"),
                render: (row) => (
                  <span className={alertState(row) === "open" ? "text-red-700 font-semibold" : ""}>
                    {alertStatusLabel(row, formatDateTime)}
                  </span>
                ),
              },
              {
                key: "actions",
                label: t("common.actions"),
                render: (row) => (!canManageSageMaster ? null : alertState(row) === "open" ? (
                  <div className="flex gap-3">
                    <button type="button" className="text-primary underline" onClick={() => setOverriding(row)}>{t("timeClock.allow")}</button>
                    <button type="button" className="text-primary underline" onClick={() => void dismiss(row)}>{t("timeClock.dismiss")}</button>
                  </div>
                ) : alertState(row) === "allowed" ? (
                  <button type="button" className="text-primary underline" onClick={() => void dismiss(row)}>{t("timeClock.dismiss")}</button>
                ) : null),
              },
            ]}
            rows={alertsQuery.data ?? []}
            empty={t("timeClock.alertsEmpty")}
          />
        )}
      </Section>

      <Section
        title={t("timeClock.flaggedTitle")}
        helper={t("timeClock.flaggedHelper")}
        action={(
          <div className="flex flex-wrap gap-3 items-center font-source">
            <label className="flex items-center gap-2">
              {t("payroll.fromLabel")}
              <input type="date" className="border rounded px-2 py-1" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="flex items-center gap-2">
              {t("payroll.toLabel")}
              <input type="date" className="border rounded px-2 py-1" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={showReviewed} onChange={(e) => setShowReviewed(e.target.checked)} />
              {t("timeClock.showReviewed")}
            </label>
          </div>
        )}
      >
        {!rangeValid ? (
          <p className="text-red-600 font-source">{t("payroll.dateOrder")}</p>
        ) : flaggedQuery.isLoading ? <Loading /> : flaggedQuery.error ? <LoadError /> : (
          <CwDataTable<CwFlaggedPunch>
            columns={[
              { key: "employee", label: t("timeClock.columnEmployee"), render: (row) => employeeName(row.workDay?.employee) },
              { key: "date", label: t("timeClock.columnDate"), render: (row) => String(row.workDay?.date ?? "").slice(0, 10) },
              { key: "punch", label: t("timeClock.columnPunch"), render: (row) => punchKindLabel(row.kind) },
              { key: "time", label: t("timeClock.columnTime"), render: (row) => formatDateTime(row.capturedAt ?? row.createdAt) },
              {
                key: "site",
                label: t("timeClock.columnJobSite"),
                render: (row) => (row.workDay?.mode === "ADMIN" ? t("timeClock.modeAdmin") : row.workDay?.jobSite?.shortName || row.workDay?.jobSite?.name || ""),
              },
              { key: "distance", label: t("timeClock.columnDistance"), render: (row) => punchDistanceLabel(row) },
              {
                key: "location",
                label: t("timeClock.columnLocation"),
                render: (row) => (row.latitude != null && row.longitude != null ? (
                  <a
                    className="text-primary underline"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`https://www.google.com/maps/search/?api=1&query=${row.latitude},${row.longitude}`}
                  >
                    {t("signInLocation.openMap")}
                  </a>
                ) : ""),
              },
              {
                key: "review",
                label: t("timeClock.columnReview"),
                render: (row) => (row.reviewedAt
                  ? t("timeClock.reviewedBy", { name: userName(row.reviewedByUser), at: formatDateTime(row.reviewedAt) })
                  : canManageSageMaster ? (
                    <button type="button" className="text-primary underline" onClick={() => void review(row)}>{t("timeClock.markReviewed")}</button>
                  ) : null),
              },
            ]}
            rows={flaggedQuery.data ?? []}
            empty={t("timeClock.flaggedEmpty")}
          />
        )}
      </Section>

      {companyId ? <SettingsSection companyId={companyId} canEdit={!!canManageSageMaster} /> : null}

      {overriding && companyId ? (
        <OverrideDialog
          alert={overriding}
          onClose={() => setOverriding(null)}
          onConfirm={async (note) => {
            await timeClockService.overrideAlert(companyId, overriding.id, note || undefined)
            refreshAlerts()
          }}
        />
      ) : null}
    </div>
  )
}
